import {
    AreaDto,
    BatteryStatus,
    DeviceDto,
    InstalledSensorStatus,
    ProjectDto,
    SignalQuality,
    StatusDto,
    SuperiorTagDto,
    SystemDto,
} from '@/core/models/types'
import { BATTERY_STATUS_OPTIONS, SIGNAL_QUALITY_OPTIONS } from '@/core/models/sensorHealth'

export type DashboardFilterName =
    | 'status'
    | 'device'
    | 'sensorType'
    | 'protocol'
    | 'project'
    | 'superiorTag'
    | 'system'
    | 'area'
    | 'batteryStatus'
    | 'signalQuality'

export interface DashboardFilterChipModel {
    key: string
    label: string
    filter: DashboardFilterName
    /** Only set for status chips; identifies which of the selected statuses the chip represents. */
    statusValue?: InstalledSensorStatus | null
}

export interface DashboardFilterSelection {
    statuses: StatusDto[]
    device: DeviceDto | null
    sensorType: string | null
    protocol: string
    project: ProjectDto | null
    superiorTag: SuperiorTagDto | null
    system: SystemDto | null
    area: AreaDto | null
    batteryStatus: BatteryStatus | null
    signalQuality: SignalQuality | null
}

// Chips are built in the same order the filters appear in FilterDialog so the row
// stays predictable as filters are added and removed.
export const buildDashboardFilterChips = (selection: DashboardFilterSelection): DashboardFilterChipModel[] => {
    const chips: DashboardFilterChipModel[] = selection.statuses
        .filter((status) => status.displayName)
        .map((status) => ({
            key: `status-${status.value}`,
            label: status.displayName as string,
            filter: 'status',
            statusValue: status.value,
        }))

    const deviceLabel = selection.device?.displayName ?? selection.device?.title
    const batteryLabel = BATTERY_STATUS_OPTIONS.find((option) => option.value === selection.batteryStatus)?.label
    const signalLabel = SIGNAL_QUALITY_OPTIONS.find((option) => option.value === selection.signalQuality)?.label

    const singleValueChips: [DashboardFilterName, string | null | undefined, string][] = [
        ['device', deviceLabel, 'Device'],
        ['sensorType', selection.sensorType, 'Type'],
        ['protocol', selection.protocol, 'Protocol'],
        ['project', selection.project?.projectCode, 'Project'],
        ['superiorTag', selection.superiorTag?.superiorTag, 'Superior tag'],
        ['system', selection.system?.systemCode, 'System'],
        ['area', selection.area?.areaCode, 'Area'],
        ['batteryStatus', batteryLabel, 'Battery'],
        ['signalQuality', signalLabel, 'Signal'],
    ]

    singleValueChips.forEach(([filter, value, prefix]) => {
        if (!value) return
        chips.push({ key: filter, label: prefix ? `${prefix}: ${value}` : value, filter })
    })

    return chips
}

// The Filter button badge counts filter *values*, not filter categories: status is a
// multi-select, so picking "Active sensors" and "Prepared sensors" counts as two.
// Derived from the selection rather than from the chips so a value without a display
// label still counts.
export const countActiveDashboardFilters = (selection: DashboardFilterSelection): number =>
    selection.statuses.length +
    [
        selection.device,
        selection.sensorType,
        selection.protocol,
        selection.project,
        selection.superiorTag,
        selection.system,
        selection.area,
        selection.batteryStatus,
        selection.signalQuality,
    ].filter(Boolean).length
